import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateTransferConfigPayload, TransferConfig, UpdateTransferConfigPayload } from '../types/banking';
import {
  createTransferConfig,
  deleteTransferConfig,
  getTransferConfigs,
  updateTransferConfig,
} from '../services/banking';

export const TRANSFER_CONFIGS_QUERY_KEY = 'transferConfigs';

export const useTransferConfigs = (companyId?: number) => {
  return useQuery<TransferConfig[]>({
    queryKey: [TRANSFER_CONFIGS_QUERY_KEY, companyId],
    queryFn: () => getTransferConfigs(companyId!),
    enabled: !!companyId,
  });
};

export const useCreateTransferConfig = (companyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (transferConfigData: CreateTransferConfigPayload) => {
      if (!companyId) {
        throw new Error('Company ID is required to create a transfer');
      }
      return createTransferConfig(companyId, transferConfigData);
    },
    onSuccess: (newTransferConfig) => {
      queryClient.setQueryData(
        [TRANSFER_CONFIGS_QUERY_KEY, companyId],
        (oldData: TransferConfig[] | undefined) => [...(oldData || []), newTransferConfig]
      );
    },
  });
};

export const useUpdateTransferConfig = (companyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      transferConfigId,
      transferConfigData,
    }: {
      transferConfigId: number;
      transferConfigData: UpdateTransferConfigPayload;
    }) => {
      return updateTransferConfig(companyId, transferConfigId, transferConfigData);
    },
    onSuccess: (updatedTransferConfig) => {
      queryClient.setQueryData(
        [TRANSFER_CONFIGS_QUERY_KEY, companyId],
        (oldData: TransferConfig[] | undefined) =>
          oldData?.map((config) => (config.id === updatedTransferConfig.id ? updatedTransferConfig : config))
      );
    },
  });
};

export const useDeleteTransferConfig = (companyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (transferConfigId: number) => {
      return deleteTransferConfig(companyId, transferConfigId);
    },
    onSuccess: (_, transferConfigId) => {
      queryClient.setQueryData(
        [TRANSFER_CONFIGS_QUERY_KEY, companyId],
        (oldData: TransferConfig[] | undefined) => oldData?.filter((config) => config.id !== transferConfigId)
      );
    },
  });
};
